import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-xl border border-[#E2DDD3] bg-white px-4 py-2.5 text-sm text-[#0B1E17] focus:outline-none focus:ring-2 focus:ring-[#1B5E46]/40 focus:border-[#1B5E46] transition";
const EMPTY_LOC = { name: "", city: "", host: "", charity: "", charity_blurb: "", status: "coming_soon", start_date: "" };

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("gg_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1D16] flex items-center justify-center px-5 grain relative" data-testid="admin-login">
      <form onSubmit={submit} className="relative w-full max-w-sm bg-[#F7F5F0] rounded-3xl p-8 space-y-4">
        <Logo />
        <p className="text-xs text-[#52665E]">Admin access — Give &amp; Grind HQ</p>
        <input required type="email" data-testid="admin-email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input required type="password" data-testid="admin-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <button type="submit" disabled={busy} data-testid="admin-login-btn"
          className="w-full rounded-full bg-[#0F2E23] text-[#F7F5F0] font-bold py-3 text-sm hover:bg-[#1B5E46] disabled:opacity-60 transition-colors">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function LocationForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form, file); }} data-testid="location-form"
      className="bg-white rounded-2xl border border-[#E2DDD3] p-6 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Location name*" value={form.name} onChange={set("name")} className={inputCls} data-testid="loc-name" />
        <input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="loc-city" />
        <input placeholder="Host company" value={form.host} onChange={set("host")} className={inputCls} data-testid="loc-host" />
        <input placeholder="Charity" value={form.charity} onChange={set("charity")} className={inputCls} data-testid="loc-charity" />
        <select value={form.status} onChange={set("status")} className={inputCls} data-testid="loc-status">
          <option value="coming_soon">Coming soon</option>
          <option value="live">Live</option>
        </select>
        <input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="loc-start-date" />
      </div>
      <textarea placeholder="Why this charity (short blurb)" value={form.charity_blurb} onChange={set("charity_blurb")} rows={3} className={inputCls} data-testid="loc-blurb" />
      <div>
        <label className="block text-xs font-bold text-[#52665E] mb-1.5">Charity logo (png, jpg, webp or svg — max 2MB)</label>
        <input type="file" accept="image/*" data-testid="loc-logo" onChange={(e) => setFile(e.target.files[0] || null)}
          className="text-sm text-[#52665E] file:mr-3 file:rounded-full file:border-0 file:bg-[#0F2E23] file:text-[#F7F5F0] file:font-bold file:px-4 file:py-2 file:text-xs hover:file:bg-[#1B5E46]" />
      </div>
      <div className="flex gap-3">
        <button type="submit" data-testid="loc-save-btn" className="rounded-full bg-[#0F2E23] text-[#F7F5F0] font-bold px-6 py-2.5 text-sm hover:bg-[#1B5E46] transition-colors">Save</button>
        <button type="button" onClick={onCancel} data-testid="loc-cancel-btn" className="rounded-full border border-[#E2DDD3] px-6 py-2.5 text-sm font-bold text-[#52665E]">Cancel</button>
      </div>
    </form>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("locations");
  const [locations, setLocations] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [weekly, setWeekly] = useState({ location_id: "", week_of: "", cups: "", raised: "" });

  const load = async () => {
    const [locs, enq] = await Promise.all([api.get("/locations"), api.get("/admin/enquiries")]);
    setLocations(locs.data);
    setEnquiries(enq.data);
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => { if (user) load().catch(() => {}); }, [user]);

  if (user === undefined) return <p className="pt-40 text-center text-sm text-[#52665E]">Loading…</p>;
  if (!user) return <Login onLogin={setUser} />;

  const saveLocation = async (form, file) => {
    try {
      const { data: loc } = editing && editing.id
        ? await api.put(`/admin/locations/${editing.id}`, form)
        : await api.post("/admin/locations", form);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/locations/${loc.id}/logo`, fd);
      }
      toast.success("Location saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteLocation = async (id) => {
    await api.delete(`/admin/locations/${id}`);
    toast.success("Location deleted");
    load();
  };

  const saveWeekly = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/weekly", { ...weekly, cups: Number(weekly.cups), raised: Number(weekly.raised) });
      toast.success("Weekly entry saved");
      setWeekly({ location_id: "", week_of: "", cups: "", raised: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const tabs = [["locations", "Locations"], ["weekly", "Weekly entry"], ["enquiries", `Enquiries (${enquiries.length})`]];

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="admin-panel">
      <header className="bg-[#0B1D16] text-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Logo dark />
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#FAF9F6]/50">{user.email}</span>
            <button data-testid="admin-logout-btn" onClick={() => { localStorage.removeItem("gg_token"); setUser(null); }}
              className="text-xs font-bold text-[#D97706] hover:text-[#F59E0B]">Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {tabs.map(([v, label]) => (
            <button key={v} data-testid={`admin-tab-${v}`} onClick={() => setTab(v)}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === v ? "bg-[#0F2E23] text-[#F7F5F0]" : "bg-white border border-[#E2DDD3] text-[#52665E]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "locations" && (
          <div className="space-y-5">
            {editing !== null ? (
              <LocationForm initial={editing} onSave={saveLocation} onCancel={() => setEditing(null)} />
            ) : (
              <button data-testid="add-location-btn" onClick={() => setEditing(EMPTY_LOC)}
                className="rounded-full bg-[#D97706] text-white font-bold px-6 py-2.5 text-sm hover:bg-[#F59E0B] transition-colors">
                + Add location
              </button>
            )}
            <div className="grid gap-3" data-testid="admin-location-list">
              {locations.map((l) => (
                <div key={l.id} className="bg-white rounded-2xl border border-[#E2DDD3] px-5 py-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`admin-loc-${l.slug}`}>
                  <div className="flex items-center gap-3">
                    {l.logo_url && (
                      <img src={`${BACKEND}${l.logo_url}`} alt="" className="w-9 h-9 rounded-lg object-contain bg-[#F7F5F0] border border-[#E2DDD3]" data-testid={`admin-loc-logo-${l.slug}`} />
                    )}
                    <div>
                      <p className="font-display font-bold text-base text-[#0B1E17]">{l.name} <span className="text-xs text-[#52665E] font-normal">/l/{l.slug}</span></p>
                      <p className="text-xs text-[#52665E]">{l.city} · {l.charity} · {l.cups.toLocaleString()} cups · £{l.raised.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono-brand uppercase tracking-[0.12em] rounded-full px-3 py-1.5 ${l.status === "live" ? "bg-[#34D399]/15 text-[#1B5E46]" : "bg-[#D97706]/10 text-[#D97706]"}`}>
                      {l.status === "live" ? "Live" : "Coming soon"}
                    </span>
                    <button data-testid={`admin-loc-edit-${l.slug}`} onClick={() => setEditing({ ...EMPTY_LOC, ...l })}
                      className="text-xs font-bold text-[#1B5E46] hover:underline">Edit</button>
                    <button data-testid={`admin-loc-delete-${l.slug}`} onClick={() => deleteLocation(l.id)}
                      className="text-xs font-bold text-red-500 hover:underline">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "weekly" && (
          <form onSubmit={saveWeekly} data-testid="weekly-form" className="bg-white rounded-2xl border border-[#E2DDD3] p-6 max-w-xl space-y-3">
            <select required value={weekly.location_id} data-testid="weekly-location"
              onChange={(e) => setWeekly({ ...weekly, location_id: e.target.value })} className={inputCls}>
              <option value="">Select location…</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <input required type="date" value={weekly.week_of} data-testid="weekly-date"
              onChange={(e) => setWeekly({ ...weekly, week_of: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input required type="number" min="0" placeholder="Cups poured" value={weekly.cups} data-testid="weekly-cups"
                onChange={(e) => setWeekly({ ...weekly, cups: e.target.value, raised: (Number(e.target.value) * 0.22).toFixed(2) })} className={inputCls} />
              <input required type="number" step="0.01" min="0" placeholder="£ raised" value={weekly.raised} data-testid="weekly-raised"
                onChange={(e) => setWeekly({ ...weekly, raised: e.target.value })} className={inputCls} />
            </div>
            <p className="text-xs text-[#52665E]">£ raised auto-fills at 22p per cup — adjust if needed.</p>
            <button type="submit" data-testid="weekly-save-btn"
              className="rounded-full bg-[#0F2E23] text-[#F7F5F0] font-bold px-6 py-2.5 text-sm hover:bg-[#1B5E46] transition-colors">
              Save entry
            </button>
          </form>
        )}

        {tab === "enquiries" && (
          <div className="grid gap-3" data-testid="admin-enquiry-list">
            {enquiries.length === 0 && <p className="text-sm text-[#52665E]">No enquiries yet.</p>}
            {enquiries.map((q) => (
              <div key={q.id} className="bg-white rounded-2xl border border-[#E2DDD3] p-5" data-testid={`enquiry-${q.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <p className="font-display font-bold text-base text-[#0B1E17]">{q.company} <span className="text-xs font-normal text-[#52665E]">({q.headcount} people{q.venue_type ? ` · ${q.venue_type}` : ""})</span></p>
                  <p className="text-xs text-[#52665E]">{new Date(q.created_at).toLocaleDateString("en-GB")}</p>
                </div>
                <p className="text-sm text-[#52665E] mt-1">{q.location} · {q.contact_name} · {q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
                {q.charity_ideas && <p className="text-sm text-[#1B5E46] mt-1">Charity ideas: {q.charity_ideas}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
